import type {
  CoreClientPaymentsExecuteParams,
  CoreClientPaymentsMarkPaidParams,
  CoreClientPaymentsReadParams,
  CoreClientPaymentsUpdateParams,
  CoreCoachPaymentsCreateRefundPaymentParams,
  CoreCoachPaymentsMarkRefundedParams,
  CoreCoachPaymentsRefundParams,
} from '../types/core/methods';
import type {RefundApiModel} from './refund';
import type {PaymentApiModel} from './types';

import {apiRequest} from '../utils/apiRequest';
import {EntityStore} from '../utils/entityStore';
import {expandObj} from '../utils/expand';

class CoachPaymentsStore<
  T extends PaymentApiModel = PaymentApiModel,
> extends EntityStore<T> {
  async createRefundPayment(
    payment: T,
    params?: Omit<CoreCoachPaymentsCreateRefundPaymentParams, 'payment_id'>,
  ) {
    const result = await apiRequest<CoreCoachPaymentsCreateRefundPaymentParams>(
      {
        method: 'coach.payments.create_refund_payment',
        params: {
          ...params,
          payment_id: payment._id,
        },
      },
    );

    let refund: RefundApiModel;

    if (params && params.expand) {
      refund = expandObj<T>(result.refund, {
        expand: params.expand,
        expanded: result._expanded,
      });
    } else {
      refund = result.refund;
    }

    const _payment = this._processResult(result, params);

    return {
      clientSecret: result.client_secret as string | undefined,
      payment: _payment,
      refund,
    };
  }

  async markRefunded(
    payment: T,
    params?: Omit<CoreCoachPaymentsMarkRefundedParams, '_id'>,
  ) {
    const result = await apiRequest<CoreCoachPaymentsMarkRefundedParams>({
      method: 'coach.payments.mark_refunded',
      params: {
        ...params,
        _id: payment._id,
      },
    });

    let refund: RefundApiModel;

    if (params && params.expand) {
      refund = expandObj<T>(result.refund, {
        expand: params.expand,
        expanded: result._expanded,
      });
    } else {
      refund = result.refund;
    }

    return refund;
  }

  async refund(
    payment: T,
    params?: Omit<CoreCoachPaymentsRefundParams, '_id'>,
  ) {
    const result = await apiRequest<CoreCoachPaymentsRefundParams>({
      method: 'coach.payments.refund',
      params: {
        ...params,
        _id: payment._id,
      },
    });

    let refund: RefundApiModel;

    if (params && params.expand) {
      refund = expandObj<T>(result.refund, {
        expand: params.expand,
        expanded: result._expanded,
      });
    } else {
      refund = result.refund;
    }

    return refund;
  }
}

export class PaymentStore<
  T extends PaymentApiModel = PaymentApiModel,
> extends EntityStore<
  T,
  CoreClientPaymentsReadParams,
  object,
  CoreClientPaymentsUpdateParams
> {
  coach = new CoachPaymentsStore<T>('payment', {
    fetch: 'coach.payments.read',
  });
  markPaid = this._createMethod<CoreClientPaymentsMarkPaidParams>({
    method: 'client.payments.mark_paid',
  });
  markPaidBatch = this._createBatchMethod<CoreClientPaymentsMarkPaidParams>({
    method: 'client.payments.mark_paid',
  });

  async execute(
    payment: T,
    params?: Omit<CoreClientPaymentsExecuteParams, '_id'>,
  ) {
    const result = await apiRequest<CoreClientPaymentsExecuteParams>({
      method: 'client.payments.execute',
      params: {
        ...params,
        _id: payment._id,
      },
    });

    const _payment = this._processResult(result, params);

    return {
      clientSecret: result.client_secret as string | undefined,
      payment: _payment,
    };
  }

  async executeBatch(
    items: {
      params?: Omit<CoreClientPaymentsExecuteParams, '_id'>;
      payment: T;
    }[],
  ) {
    const results = (await apiRequest<CoreClientPaymentsExecuteParams>({
      batch: items.map(item => ({
        method: 'client.payments.execute',
        params: {
          ...item.params,
          _id: item.payment._id,
        },
      })),
    })) as any[];

    const executedPayments = await this._processBatchResult(results, items);

    return executedPayments.map((payment, i) => ({
      clientSecret: results[i].client_secret as string | undefined,
      payment,
    }));
  }
}

export const paymentStore = new PaymentStore('payment', {
  fetch: 'client.payments.read',
  update: 'client.payments.update',
});
